import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronDown,
  ChevronRight,
  Check,
  Link2,
  BookOpen,
  GraduationCap,
  Baby,
  School,
  Building,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { api } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import { CONFIRM_DIALOG_OVERLAY_CLASS } from './ui/modalOverlay';
import { BoiteACocher } from './ui/CaseACocher';

const ClassAssociationModal = ({
  isOpen,
  onClose,
  onSave,
  initialSelections = [],
  matiereId = null,
  coefficientDefaut = 1,
  title = "Associer des classes",
  subtitle = "Sélectionnez les classes à associer",
  anneeScolaireId = null,
  sectionsData = [],
  classesData = [],
  // Sans le droit d'associer (CLASSES.ASSOCIATIONS.MANAGE, ou la porte du
  // module tant que la clé manque au catalogue) : on consulte, rien ne s'écrit.
  lectureSeule = false
}) => {
  // ============================================================================
  // ÉTATS LOCAUX
  // ============================================================================
  const [sections, setSections] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [initialSelectionsState, setInitialSelectionsState] = useState([]);
  const [coefficients, setCoefficients] = useState({});
  const [initialCoefficients, setInitialCoefficients] = useState({});  // ⬅️ AJOUTÉ
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ============================================================================
  // ICÔNES PAR SECTION
  // ============================================================================
  const getSectionIcon = (sectionName) => {
    const name = sectionName.toLowerCase();
    if (name.includes('préscolaire') || name.includes('prescolaire') || name.includes('maternelle')) {
      return <Baby className="w-5 h-5" />;
    }
    if (name.includes('primaire') || name.includes('élémentaire')) {
      return <BookOpen className="w-5 h-5" />;
    }
    if (name.includes('collège') || name.includes('college')) {
      return <School className="w-5 h-5" />;
    }
    if (name.includes('lycée') || name.includes('lycee') || name.includes('secondaire')) {
      return <Building className="w-5 h-5" />;
    }
    return <GraduationCap className="w-5 h-5" />;
  };

  // ============================================================================
  // CHARGEMENT DES DONNÉES
  // ============================================================================
  const loadData = useCallback(async () => {
    setLoading(true);
    try {

      const classesResponse = classesData.length > 0 ? classesData : await api.classes.getAll();

      const sectionsResolved = [...sectionsData].sort((a, b) => {
        if (a.ordre && b.ordre) return a.ordre - b.ordre;
        if (a.ordre) return -1;
        if (b.ordre) return 1;
        return a.nom.localeCompare(b.nom);
      });

      const classesResolved = Array.isArray(classesResponse)
        ? classesResponse
        : classesResponse.success
          ? classesResponse.data
          : [];

      setSections(sectionsResolved);
      setClasses(classesResolved);

      if (sectionsResolved.length > 0) {
        setExpandedSections(prev => prev.size > 0 ? prev : new Set([sectionsResolved[0].id]));
      }

      if (matiereId) {
        const associations = await api.matieres.associations.getByMatiere(
          matiereId,
          anneeScolaireId
        );


        const coeffsMap = {};
        associations.forEach(assoc => {
          coeffsMap[assoc.classe_id] = assoc.coefficient_effectif || coefficientDefaut;
        });

        setCoefficients(prev => ({ ...prev, ...coeffsMap }));
        setInitialCoefficients({ ...coeffsMap });
      }


    } catch (error) {
      console.error('[ClassModal] Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  }, [anneeScolaireId, classesData, coefficientDefaut, matiereId, sectionsData]);
  // ============================================================================
  // EFFECTS
  // ============================================================================
  useEffect(() => {
    if (isOpen) {
      setSelectedClassIds([...initialSelections]);
      setInitialSelectionsState([...initialSelections]);
      setExpandedSections(new Set());

      const initialCoeffs = {};
      initialSelections.forEach(classeId => {
        initialCoeffs[classeId] = coefficientDefaut;
      });
      setCoefficients(initialCoeffs);
      setInitialCoefficients(initialCoeffs);

      loadData();
    }
  }, [isOpen, initialSelections, coefficientDefaut, loadData]);

  // ============================================================================
  // LOGIQUE DE SÉLECTION
  // ============================================================================

  // Grouper les classes par section
  const classesBySection = classes.reduce((acc, classe) => {
    const sectionId = classe.section_id;
    if (!acc[sectionId]) {
      acc[sectionId] = [];
    }
    acc[sectionId].push(classe);
    return acc;
  }, {});

  // Vérifier si toute une section est sélectionnée
  const isSectionFullySelected = (sectionId) => {
    const sectionClasses = classesBySection[sectionId] || [];
    return sectionClasses.length > 0 &&
           sectionClasses.every(classe => selectedClassIds.includes(classe.id));
  };

  // Vérifier si une section est partiellement sélectionnée
  const isSectionPartiallySelected = (sectionId) => {
    const sectionClasses = classesBySection[sectionId] || [];
    return sectionClasses.some(classe => selectedClassIds.includes(classe.id)) &&
           !isSectionFullySelected(sectionId);
  };

  // Toggle section complète
  const toggleSection = (sectionId) => {
    const sectionClasses = classesBySection[sectionId] || [];
    const sectionClassIds = sectionClasses.map(c => c.id);

    if (isSectionFullySelected(sectionId)) {
      // Désélectionner toute la section
      setSelectedClassIds(prev => prev.filter(id => !sectionClassIds.includes(id)));
    } else {
      // Sélectionner toute la section
      setSelectedClassIds(prev => {
        const newSelection = [...prev];
        sectionClassIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
            // Initialiser le coefficient au défaut
            if (!coefficients[id]) {
              setCoefficients(prevCoeffs => ({
                ...prevCoeffs,
                [id]: coefficientDefaut
              }));
            }
          }
        });
        return newSelection;
      });
    }
  };

  // Toggle classe individuelle
  const toggleClass = (classId) => {
    setSelectedClassIds(prev => {
      if (prev.includes(classId)) {
        return prev.filter(id => id !== classId);
      } else {
        // Initialiser le coefficient au défaut lors de la sélection
        if (!coefficients[classId]) {
          setCoefficients(prevCoeffs => ({
            ...prevCoeffs,
            [classId]: coefficientDefaut
          }));
        }
        return [...prev, classId];
      }
    });
  };

  // Mettre à jour un coefficient
  const updateCoefficient = (classId, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setCoefficients(prev => ({
        ...prev,
        [classId]: numValue
      }));
    }
  };

  // ⬅️ NOUVELLE FONCTION : Vérifier si les coefficients ont changé
  const hasCoefficientsChanged = () => {
    // Vérifier pour toutes les classes actuellement sélectionnées
    for (const classId of selectedClassIds) {
      const currentCoeff = parseFloat(coefficients[classId] ?? coefficientDefaut);
      const initialCoeff = parseFloat(initialCoefficients[classId] ?? coefficientDefaut);

      if (Number.isFinite(currentCoeff) && Number.isFinite(initialCoeff) && currentCoeff !== initialCoeff) {
        return true;
      }
    }
    return false;
  };

  // Toggle expansion de section
  const toggleExpansion = (sectionId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  // ============================================================================
  // GESTION DE LA SAUVEGARDE
  // ============================================================================
  const handleSave = async () => {
    if (saving || lectureSeule) return;

    setSaving(true);

    try {
      // Envoyer aussi les coefficients et attendre la réponse
      const saveResult = await onSave(selectedClassIds, coefficients);
      if (saveResult?.success === false) {
        return;
      }

      // Mettre à jour les sélections initiales après sauvegarde
      setInitialSelectionsState([...selectedClassIds]);

      // ⬅️ AJOUTÉ - Mettre à jour les coefficients initiaux après sauvegarde
      setInitialCoefficients({ ...coefficients });

      // Recharger les données pour mettre à jour les badges
      await loadData();


    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedClassIds([...initialSelections]);
    onClose();
  };

  // ============================================================================
  // RENDU
  // ============================================================================
  if (!isOpen) return null;

  const classesToAdd = selectedClassIds.filter(id => !initialSelectionsState.includes(id));
  const classesToRemove = initialSelectionsState.filter(id => !selectedClassIds.includes(id));
  const unchangedClasses = selectedClassIds.filter(id => initialSelectionsState.includes(id));
  const coefficientsChanged = hasCoefficientsChanged();
  const hasChanges = classesToAdd.length > 0 || classesToRemove.length > 0 || coefficientsChanged;

  return createPortal(
    <div className={`${CONFIRM_DIALOG_OVERLAY_CLASS} flex items-center justify-center p-4`}>
        <Card className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden bg-white shadow-2xl">
          <CardHeader className="border-b border-[#246990]/10 bg-[#246990]/5">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl text-[#246990] flex items-center">
                  <Link2 className="w-6 h-6 mr-2" />
                  {title}
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
              </div>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Statistiques */}
            <div className="flex items-center space-x-6 mt-4 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-gray-600">
                  {initialSelectionsState.length} initialement associée{initialSelectionsState.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-gray-600">
                  {selectedClassIds.filter(id => !initialSelectionsState.includes(id)).length} à ajouter
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-gray-600">
                  {initialSelectionsState.filter(id => !selectedClassIds.includes(id)).length} à supprimer
                </span>
              </div>
              <div className="text-gray-500">
                Résultat: {selectedClassIds.length} classe{selectedClassIds.length > 1 ? 's' : ''}
              </div>
            </div>
          </CardHeader>

          <CardContent className="flex-1 overflow-y-auto p-0">
            {loading ? (
              <LoadingSpinner message="Chargement des classes..." size="sm" color="#246990" />
            ) : (
              <div className="space-y-1">
                {sections.map(section => {
                  const sectionClasses = classesBySection[section.id] || [];
                  const isExpanded = expandedSections.has(section.id);
                  const isFullySelected = isSectionFullySelected(section.id);
                  const isPartiallySelected = isSectionPartiallySelected(section.id);

                  return (
                    <div key={section.id} className="border-b border-gray-100 last:border-b-0">
                      {/* En-tête de section */}
                      <div
                        className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => toggleExpansion(section.id)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="text-gray-400">
                            {isExpanded ? (
                              <ChevronDown className="w-5 h-5" />
                            ) : (
                              <ChevronRight className="w-5 h-5" />
                            )}
                          </div>
                          <div className="text-[#246990]">
                            {getSectionIcon(section.nom)}
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">{section.nom}</h3>
                            <p className="text-sm text-gray-500">
                              {sectionClasses.length} classe{sectionClasses.length > 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3">
                          {isPartiallySelected && (
                            <span className="text-xs text-orange-600 font-medium">
                              Partielle
                            </span>
                          )}
                          {/* La case de KLASYS (ui/CaseACocher, DESIGN-SYSTEM.md), partielle
                              quand une partie seulement des classes est cochée. */}
                          <label
                            className={`flex items-center gap-2 ${lectureSeule ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <BoiteACocher
                              checked={isFullySelected}
                              indetermine={isPartiallySelected}
                              onChange={() => toggleSection(section.id)}
                              disabled={lectureSeule}
                            />
                            <span className={`text-sm font-medium ${isFullySelected ? 'text-gray-900' : 'text-gray-700'}`}>
                              Tout sélectionner
                            </span>
                          </label>
                        </div>
                      </div>

                      {/* Classes de la section */}
                      {isExpanded && (
                        <div className="bg-gray-25 px-4 pb-4">
                          <div className="grid grid-cols-1 gap-3">
                            {sectionClasses
                              .sort((a, b) => {
                                if (a.ordre && b.ordre) return a.ordre - b.ordre;
                                return a.nom.localeCompare(b.nom);
                              })
                              .map(classe => {
                                const isSelected = selectedClassIds.includes(classe.id);
                                const wasInitiallyAssociated = initialSelectionsState.includes(classe.id);
                                const isNewAssociation = isSelected && !wasInitiallyAssociated;
                                const willBeRemoved = !isSelected && wasInitiallyAssociated;

                                return (
                                  <label
                                    key={classe.id}
                                    className={`flex items-center space-x-3 p-3 rounded-lg border transition-all ${lectureSeule ? 'cursor-not-allowed' : 'cursor-pointer'} ${
                                      isSelected
                                        ? wasInitiallyAssociated
                                          ? 'border-blue-400 bg-blue-50'
                                          : 'border-green-400 bg-green-50'
                                        : wasInitiallyAssociated
                                          ? 'border-red-300 bg-red-50'
                                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                    }`}
                                  >
                                    {/* La case de KLASYS (ui/CaseACocher) : toute la ligne la coche. */}
                                    <BoiteACocher
                                      checked={isSelected}
                                      onChange={() => toggleClass(classe.id)}
                                      disabled={lectureSeule}
                                    />

                                    {/* Info classe */}
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-gray-900">{classe.nom}</p>
                                      <p className="text-sm text-gray-500">
                                        {classe.effectif || 0} élève{(classe.effectif || 0) > 1 ? 's' : ''}
                                      </p>

                                      {/* Badges d'état */}
                                      <div className="mt-1">
                                        {wasInitiallyAssociated && isSelected && (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            Déjà associée
                                          </span>
                                        )}
                                        {isNewAssociation && (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            Sera associée
                                          </span>
                                        )}
                                        {willBeRemoved && (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            Sera dissociée
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Input coefficient */}
                                    {isSelected && (
                                      <div className="flex items-center space-x-2">
                                        <span className="text-sm font-medium text-gray-700">
                                          Coef:
                                        </span>
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.5"
                                          value={coefficients[classe.id] || coefficientDefaut}
                                          onChange={(e) => updateCoefficient(classe.id, e.target.value)}
                                          onClick={(e) => e.stopPropagation()}
                                          readOnly={lectureSeule}
                                          className={'w-16 px-2 py-1 text-center border border-gray-300 rounded-md focus:border-[#246990] focus:ring-1 focus:ring-[#246990] outline-none' + (lectureSeule ? ' bg-gray-100 text-gray-600' : '')}
                                        />
                                      </div>
                                    )}

                                    {/* Icône check */}
                                    {isSelected && (
                                      <Check className={`w-5 h-5 ${
                                        wasInitiallyAssociated ? 'text-blue-600' : 'text-green-600'
                                      }`} />
                                    )}
                                    {willBeRemoved && (
                                      <X className="w-5 h-5 text-red-600" />
                                    )}
                                  </label>
                                );
                              })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>

          {/* Footer */}
          <div className="border-t border-gray-100 bg-gray-50 px-6 py-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="text-sm text-gray-600">
                {lectureSeule ? (
                  <span className="text-amber-800">
                    Lecture seule : associer cette matière à une classe ou changer son coefficient demande la
                    permission « Associer les matières aux classes ».
                  </span>
                ) : !hasChanges ? (
                  <span>Aucune modification</span>
                ) : (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    {unchangedClasses.length > 0 && (
                      <span className="text-blue-600">{unchangedClasses.length} conservée(s)</span>
                    )}
                    {classesToAdd.length > 0 && (
                      <span className="font-medium text-green-600">{classesToAdd.length} à associer</span>
                    )}
                    {classesToRemove.length > 0 && (
                      <span className="font-medium text-red-600">{classesToRemove.length} à dissocier</span>
                    )}
                    {coefficientsChanged && (
                      <span className="font-medium text-orange-600">Coefficient(s) modifié(s)</span>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={saving}
                  className="inline-flex min-w-[110px] items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-[#246990]/30 hover:text-[#246990] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Fermer
                </button>

                {hasChanges && !lectureSeule && (
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex min-w-[132px] items-center justify-center rounded-lg bg-[#246990] px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#1f5a7d] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Enregistrement...
                      </>
                    ) : (
                      'Enregistrer'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </Card>
    </div>,
    document.body
  );
};

export default ClassAssociationModal;